import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';

import {
  TabDefaultOption, TabOption, TabOptions, TabSelectedEvent, TabSize, TabType
} from '@design-system/components/molecules/tab/entities/tab.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';

@Component({
  selector: 'app-ds-tab',
  templateUrl: './tab.component.html',
  styleUrls: ['./tab.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IconDsComponent,
    TypographyDsComponent,
  ]
})
export class TabDsComponent implements OnInit, OnChanges {

  @Input() size = TabSize.RG;
  @Input() type = TabType.LineBellow;
  @Input() options: TabOptions = [null, null];
  @Input() defaultOption: TabDefaultOption = 0;
  @Input() justUseIcon = false;
  @Input() isDisabled = false;

  @Output() selectTab: EventEmitter<TabSelectedEvent> = new EventEmitter<TabSelectedEvent>();

  public readonly fontFamily = TypographyFontFamily;

  public readonly bullet = Icon.BulletA;
  public readonly bulletColor = IconFillColor.Neon;

  private selectedTab: TabDefaultOption = 0;

  constructor() { }

  ngOnInit(): void {
    this.selectedTab = this.tabs?.length === 2 ? this.defaultOption : 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.defaultOption) {
      this.selectedTab = this.tabs?.length === 2 ? changes?.defaultOption?.currentValue : 0;
    }
  }

  get enabledIcons(): boolean {
    return this.justUseIcon && this.type === TabType.Pill;
  }

  get tabs(): TabOptions {
    return this.options
      .map(option => this.enabledIcons ? (option?.Icon ? option : null) : option)
      .filter(option => !!option) as TabOptions;
  }

  get fontSize(): TypographyFontSize {
    return this.size === TabSize.RG ? TypographyFontSize.XXS :
      this.size === TabSize.SM ? TypographyFontSize.XXXS : TypographyFontSize.XS;
  }

  get tabWidth(): string {
    return this.enabledIcons ? null : this.tabs?.length === 2 ? '50%' : '100%';
  }

  get useDivider(): boolean {
    return this.type === TabType.LineBellow;
  }

  get dividerPosition(): string {
    return this.selectedTab === 0 ? '0%' : '100%';
  }

  public isSelected(index: number): boolean {
    return this.selectedTab === index;
  }

  public iconFillColor(index: number): IconFillColor {
    return this.isSelected(index) ? IconFillColor.Primary :
      this.isDisabled ? IconFillColor.Disabled : IconFillColor.Secondary;
  }

  public fontColor(index: number): TypographyColor {
    return this.isSelected(index) ? TypographyColor.Primary : (
      this.isDisabled ? TypographyColor.Disabled : (
        this.type === TabType.LineBellow ? TypographyColor.Disabled : TypographyColor.Secondary
      )
    );
  }

  public useSubtitle(tab: TabOption): boolean {
    return !!tab?.Subtitle && this.type === TabType.Pill && this.size !== TabSize.SM;
  }

  public useNotification(tab: TabOption): boolean {
    return !!tab?.HasNotifications && this.type === TabType.LineBellow;
  }

  public trackById(index: number): number {
    return index;
  }

  public selected(tab: TabOption, option: number): void {
    if (!this.isDisabled) {
      this.selectedTab = option as TabDefaultOption;
      this.selectTab.emit({ option: this.selectedTab, tab });
    }
  }

}
